import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { Button } from "~/ui-rtk/components/ui/controls"
import Select from "~/ui-rtk/components/ui/controls/Select"
import { Divider } from "~/ui-rtk/components/ui/common"
import { H3 } from "~/ui-rtk/components/ui/typography"
import { CONNECTORS_IDS } from "~/ui-rtk/constants/sources"
import useAccess from "~/ui-rtk/hooks/access"
import { useCheckServiceIsConnected } from "~/ui-rtk/hooks/connected-services"
import {
  useGetCurrentTikTokAdvertiserQuery,
  useListTikTokAdvertisersQuery,
  useSetTikTokAdvertiserMutation,
} from "~/ui-rtk/api/paidMediaApi"

/**
 * TikTok counterpart of the Meta access card's ad-account binding: picks which advertiser
 * account (of the ones the Ads Management connector authorized) AdBlaster
 * and Campaign Blaster write to. The choice is stored on the connector
 * metadata as `advertiser_id`, which the TikTok paid-media service reads
 * before falling back to the first authorized account.
 */
export default function TikTokAdvertiserSettings() {
  // Same defense in depth as the Meta access card: hidden unless super admin,
  // even if mounted somewhere without the CompanyPage guard.
  const { isSuperAdmin } = useAccess()
  const isConnected = useCheckServiceIsConnected(
    CONNECTORS_IDS.TIKTOK_ADS_MANAGEMENT,
  )
  const { data: current, isLoading: isCurrentLoading } =
    useGetCurrentTikTokAdvertiserQuery(undefined, {
      skip: !isSuperAdmin || !isConnected,
    })
  const { data: advertisers, isLoading: isListLoading } =
    useListTikTokAdvertisersQuery(undefined, {
      skip: !isSuperAdmin || !isConnected,
    })
  const [saveAdvertiser, { isLoading: isSaving }] =
    useSetTikTokAdvertiserMutation()

  const savedId = current?.advertiserId ?? null
  const [pendingId, setPendingId] = useState<string>("")

  // pendingId is intentionally excluded from deps so user selections aren't
  // clobbered on every keystroke; only re-sync on savedId/list changes.
  useEffect(() => {
    if (savedId) {
      setPendingId(savedId)
    } else if (!pendingId && advertisers && advertisers.length > 0) {
      setPendingId(advertisers[0].id)
    }
  }, [savedId, advertisers])

  const handleSave = async () => {
    if (!pendingId) return
    try {
      await saveAdvertiser({ advertiserId: pendingId }).unwrap()
      toast.success("TikTok advertiser account updated.", {
        toastId: "tiktok-advertiser-save-success",
      })
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        "Failed to update TikTok advertiser account."
      toast.error(message, { toastId: "tiktok-advertiser-save-error" })
    }
  }

  if (!isSuperAdmin) {
    return null
  }

  const isLoading = isCurrentLoading || isListLoading
  const hasAdvertisers = !!advertisers && advertisers.length > 0

  let body: JSX.Element
  if (!isConnected) {
    body = (
      <p className="py-3 text-sm text-basic-light/60">
        TikTok Ads Management is not connected. Connect it under Setup &rarr;
        Connectors to pick an advertiser account.
      </p>
    )
  } else if (isLoading) {
    body = <span className="loading loading-spinner loading-md text-primary" />
  } else if (!hasAdvertisers) {
    body = (
      <p className="py-3 text-sm text-basic-light/60">
        No advertiser accounts were authorized on this connector. Reconnect
        TikTok Ads Management and grant at least one ad account.
      </p>
    )
  } else {
    body = (
      <div className="flex items-center gap-3">
        <Select
          options={advertisers.map(a => ({
            label: a.name === a.id ? a.id : `${a.name} (${a.id})`,
            value: a.id,
          }))}
          value={pendingId}
          onChange={e => setPendingId(e.target.value)}
        />
        <Button
          variant={{ variant: "solid", color: "pink" }}
          size="sm"
          type="button"
          isLoading={isSaving}
          disabled={!pendingId || pendingId === savedId}
          onClick={() => void handleSave()}
        >
          Save
        </Button>
      </div>
    )
  }

  return (
    <>
      <Divider className="my-3" />
      <H3>TikTok Advertiser Account</H3>
      <div className="grid grid-cols-3 grid-rows-1 gap-8 py-3">
        <h3 className="row-span-2 py-3">Active Advertiser Account</h3>
        <div className="relative col-span-2">{body}</div>
      </div>
    </>
  )
}
